import { useCallback, useEffect, useMemo, useState } from 'react';
import { PlatformHeader } from '../../../../shared/platform/PlatformHeader';
import '../../../overview/ui/overview.css';
import { createFireSignalAdapter, FireSignalReadError } from '../../adapters/fire-signals/http';
import type { Bbox, Candidate, Source, WeatherLink } from '../../consumer/fire-signals/contract';
import {
  candidatePageQuery,
  FIRE_SIGNALS_ROOT,
  FIRE_SIGNALS_WARNING,
  parseBbox,
  safeSourceUrl,
  scopedQuery,
  signalHref,
  STATUS_LABELS,
  zoomBbox,
} from '../../consumer/fire-signals/contract';
import './fire-signals.css';
import { GeographicMap } from './GeographicMap';
const adapter = createFireSignalAdapter();
function useRead<T>(read: (signal: AbortSignal) => Promise<T>) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{
    key: typeof read;
    data: T | null;
    error: unknown;
    loading: boolean;
  }>({ key: read, data: null, error: null, loading: true });
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setState({ key: read, data: null, error: null, loading: true });
    void read(controller.signal).then(
      (data) => {
        if (active) setState({ key: read, data, error: null, loading: false });
      },
      (error) => {
        if (active) setState({ key: read, data: null, error, loading: false });
      },
    );
    return () => {
      active = false;
      controller.abort();
    };
  }, [read, attempt]);
  return {
    ...(state.key === read ? state : { data: null, error: null, loading: true }),
    retry: () => setAttempt((n) => n + 1),
  };
}
function ReadNotice({
  loading,
  error,
  retry,
}: {
  loading: boolean;
  error: unknown;
  retry: () => void;
}) {
  if (!loading && !error) return null;
  const failure =
    error instanceof FireSignalReadError
      ? `${error.status || 'NETWORK'} · ${error.code}`
      : '读取失败';
  return (
    <section className="fs-notice" role="status">
      {loading ? (
        '正在读取已保存的历史卫星与气象资料…'
      ) : (
        <>
          <h2>{failure}</h2>
          <p>
            {error instanceof FireSignalReadError && error.status === 404
              ? '未找到此固定候选。请核对链接或返回地图。'
              : '服务离线、请求无效或研究存储不可用。当前结果未知；无资料不表示安全。'}
          </p>
          <button onClick={retry} type="button">
            重新读取
          </button>
        </>
      )}
    </section>
  );
}
function SourceLink({ uri }: { uri: string }) {
  const safe = safeSourceUrl(uri);
  return safe ? (
    <a href={safe} target="_blank" rel="noreferrer">
      官方原始来源 ↗
    </a>
  ) : (
    <span>来源 URL 不可安全展示</span>
  );
}
function Fact({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>
        {value === null || value === undefined || value === '' ? 'UNKNOWN / 未提供' : String(value)}
      </dd>
    </div>
  );
}
function Measurement({ title, link }: { title: string; link: WeatherLink | null }) {
  return (
    <article
      className="fs-metric"
      data-weather-observation-id={link?.observationId}
      data-weather-observed-at={link?.observedAtUTC}
      data-weather-distance-km={link?.distanceKm}
      data-weather-age-minutes={link?.ageMinutes}
    >
      <h3>{title}</h3>
      {!link ? (
        <p>没有合格记录 · 不填补缺失值</p>
      ) : (
        <>
          <strong data-weather-value={link.value}>
            {link.value} {link.unit === 'degC' ? '°C' : link.unit}
          </strong>
          <dl>
            <Fact label="小时端点 UTC" value={link.observedAtUTC} />
            <Fact label="原始观测 UTC" value={link.rawObservedAtUTC} />
            <Fact label="距离 km / 滞后 min" value={`${link.distanceKm} / ${link.ageMinutes}`} />
            <Fact
              label="QC / 原始质量码 / 来源码"
              value={`${link.quality} / ${link.rawQualityCode} / ${link.sourceCode}`}
            />
            <Fact label="原始行" value={link.rawRowNumber} />
          </dl>
          <SourceLink uri={link.originalUri} />
          <details>
            <summary>不可变记录引用</summary>
            <dl>
              <Fact label="Observation ID" value={link.observationId} />
              <Fact label="Original SHA256" value={link.originalHash} />
              <Fact label="Raw row SHA256" value={link.rawRowHash} />
              <Fact label="Source revision" value={link.sourceRevision} />
            </dl>
          </details>
        </>
      )}
    </article>
  );
}
export function CandidateDetail({
  candidate: c,
  sources,
}: {
  candidate: Candidate;
  sources: Source[];
}) {
  const s = c.satellite;
  return (
    <div
      data-detail-candidate-id={c.candidateId}
      data-latitude={s.latitude}
      data-longitude={s.longitude}
      data-raw-row={s.rawRowNumber}
      data-original-hash={s.originalHash}
      data-frp-mw={s.frpMW}
      data-brightness-k={s.brightnessTi4Kelvin}
    >
      <section className="fs-card">
        <div className="fs-section-title">
          <div>
            <p className="fs-eyebrow">固定历史候选 · {c.areaRef}</p>
            <h2>
              {s.satellite} / {s.instrument}
            </h2>
          </div>
          <span className="fs-status">{STATUS_LABELS[c.association.status]}</span>
        </div>
        <p className="fs-lead" data-acquired-at={s.acquiredAtUTC}>
          {s.acquiredAtUTC} · UTC
        </p>
        <dl className="fs-facts">
          <Fact label="像元中心 · 纬度 / 经度" value={`${s.latitude} / ${s.longitude}`} />
          <Fact label="产品版本 / 坐标系" value={`${s.productVersion} / EPSG:${s.crsEPSG}`} />
          <Fact label="FRP · MW" value={s.frpMW} />
          <Fact
            label="亮温 Ti4 / Ti5 · K"
            value={`${s.brightnessTi4Kelvin ?? 'UNKNOWN'} / ${s.brightnessTi5Kelvin ?? 'UNKNOWN'}`}
          />
          <Fact
            label="像元 scan / track · km"
            value={`${s.pixelScanKm ?? 'UNKNOWN'} / ${s.pixelTrackKm ?? 'UNKNOWN'}`}
          />
          <Fact label="原生 confidence / 昼夜" value={`${s.nativeConfidence} / ${s.dayNight}`} />
        </dl>
        <p className="fs-caption">
          l / n / h 为像元算法质量；不是火灾概率。亮温是卫星通道 Kelvin，FRP
          是辐射功率。像元中心与尺寸不表示每平方米燃烧。
        </p>
      </section>
      <section className="fs-card">
        <h2>附近气象背景</h2>
        <p>
          {c.weather.station
            ? `${c.weather.station.name} · ${c.weather.station.stationId} · ${c.weather.station.latitude}, ${c.weather.station.longitude}`
            : '未选中合格气象站'}
        </p>
        <p className="fs-status">{c.association.context}</p>
        {c.association.context === 'DISTANT_REGIONAL_CONTEXT' && (
          <p>远距离区域背景（25–100 km），不代表像元现场的天气。</p>
        )}
        <p>
          规则：距离 ≤ {c.association.distanceLimitKm} km，天气不晚于卫星采集，滞后 ≤{' '}
          {c.association.timeLimitHours} h。
        </p>
        <p>{c.association.eligibilityReason}</p>
        <div className="fs-weather">
          <Measurement title="温度" link={c.weather.metrics.TEMPERATURE} />
          <Measurement title="风速" link={c.weather.metrics.WIND_SPEED} />
        </div>
        <p className="fs-caption">
          NOAA 历史接收时间：{c.weather.archiveReceivedAt}
          。小时端点与原始观测分别保留；不能推断当年的即时可用性。
        </p>
        <details>
          <summary>逐站资格与未匹配原因</summary>
          <pre>{JSON.stringify(c.association.stationDecisions, null, 2)}</pre>
        </details>
      </section>
      <section className="fs-card">
        <h2>独立事件参照</h2>
        <p>暂无可核实事件参照 · {c.eventReference.status}</p>
        <p>{c.eventReference.reason} · 没有独立事件核验，不推断无火、不计算火灾概率。</p>
      </section>
      <section className="fs-card">
        <h2>来源与复算依据</h2>
        <SourceLink uri={s.originalUri} />
        <dl className="fs-facts">
          <Fact label="NASA 原始行" value={s.rawRowNumber} />
          <Fact label="原始行 SHA256" value={s.rawRowHash} />
          <Fact label="源文件 SHA256" value={s.originalHash} />
          <Fact label="Candidate ID" value={c.candidateId} />
          <Fact label="Detection ID" value={s.detectionId} />
          <Fact label="Run ID" value={c.runId} />
          <Fact label="规则版本" value={c.ruleVersion} />
          <Fact label="规则 SHA256" value={c.ruleHash} />
          <Fact label="配置 SHA256" value={c.lifecycle.configSnapshotHash} />
        </dl>
        {sources
          .filter((source) =>
            [
              s.sourceRevision,
              c.weather.metrics.TEMPERATURE?.sourceRevision,
              c.weather.metrics.WIND_SPEED?.sourceRevision,
            ].includes(source.sourceRevision),
          )
          .map((source) => (
            <details key={source.sourceId}>
              <summary>{source.provider} · 下载与许可记录</summary>
              <dl>
                <Fact label="下载时间" value={source.retrievedAt} />
                <Fact label="平台接收时间" value={source.receivedAt} />
                <Fact label="原归档发布时间" value={source.sourceArchivePublishedAt} />
                <Fact label="Source revision" value={source.sourceRevision} />
                <Fact label="HTTP / bytes" value={`${source.httpStatus} / ${source.bytes}`} />
                <Fact
                  label="许可 / 用途"
                  value={
                    typeof source.license === 'string'
                      ? source.license
                      : JSON.stringify(source.license)
                  }
                />
              </dl>
              <SourceLink uri={source.originalUri} />
            </details>
          ))}
        <p className="fs-caption">NASA FIRMS 官方历史教程样本 / NOAA NCEI。引用不表示机构背书。</p>
      </section>
    </div>
  );
}
function updateHref(search: string, changes: Record<string, string | null>) {
  const q = new URLSearchParams(search);
  for (const [k, v] of Object.entries(changes)) {
    if (v === null) q.delete(k);
    else q.set(k, v);
  }
  return signalHref(q.toString());
}
function ListPage({ search }: { search: string }) {
  const scope = useMemo(() => scopedQuery(search).toString(), [search]);
  const params = new URLSearchParams(search);
  const offsetRaw = params.get('offset') ?? '0';
  const offset = /^\d+$/.test(offsetRaw) ? Number(offsetRaw) : -1;
  const read = useCallback(
    async (signal: AbortSignal) => {
      const [summary, page] = await Promise.all([
        adapter.summary(scope, signal),
        adapter.candidates(candidatePageQuery(scope, offset), signal),
      ]);
      return { summary, page };
    },
    [scope, offset],
  );
  const { data, error, loading, retry } = useRead(read);
  const selectedBox = parseBbox(params.get('bbox'));
  const area = data?.summary.areas.find((a) => a.areaRef === params.get('areaRef'));
  const boxes = area ? [area.bbox] : (data?.summary.areas.map((a) => a.bbox) ?? []);
  const bbox: Bbox =
    selectedBox ??
    (boxes.length
      ? [
          Math.min(...boxes.map((b) => b[0])),
          Math.min(...boxes.map((b) => b[1])),
          Math.max(...boxes.map((b) => b[2])),
          Math.max(...boxes.map((b) => b[3])),
        ]
      : [-180, -90, 180, 90]);
  return (
    <>
      <form
        className="fs-filters"
        action={FIRE_SIGNALS_ROOT}
        method="get"
        onSubmit={(event) => {
          event.preventDefault();
          const query = new URLSearchParams();
          for (const [key, value] of new FormData(event.currentTarget))
            if (typeof value === 'string' && value.trim()) query.append(key, value.trim());
          window.location.assign(signalHref(query.toString()));
        }}
      >
        <label>
          研究区域
          <select name="areaRef" defaultValue={params.get('areaRef') ?? ''}>
            <option value="">全部研究区</option>
            {data?.summary.areas.map((a) => (
              <option key={a.areaRef} value={a.areaRef}>
                {a.name}
              </option>
            ))}
            {params.get('areaRef') &&
              !data?.summary.areas.some((a) => a.areaRef === params.get('areaRef')) && (
                <option value={params.get('areaRef') ?? ''}>{params.get('areaRef')}</option>
              )}
          </select>
        </label>
        <label>
          采集起始 UTC
          <input
            name="from"
            placeholder="2023-07-12T00:00:00Z"
            defaultValue={params.get('from') ?? ''}
          />
        </label>
        <label>
          采集截止 UTC
          <input
            name="to"
            placeholder="2023-07-13T00:00:00Z"
            defaultValue={params.get('to') ?? ''}
          />
        </label>
        <label>
          关联状态
          <select name="status" defaultValue={params.get('status') ?? ''}>
            <option value="">全部状态</option>
            {Object.entries(STATUS_LABELS).map(([key, label]) => (
              <option value={key} key={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          仪器
          <input
            name="instrument"
            placeholder="例如 VIIRS"
            defaultValue={params.get('instrument') ?? ''}
          />
        </label>
        <label>
          地理范围 W,S,E,N
          <input
            name="bbox"
            placeholder="西经,南纬,东经,北纬"
            defaultValue={params.get('bbox') ?? ''}
          />
        </label>
        {['runId', 'provider', 'stationId', 'unit', 'version'].map((k) =>
          params.has(k) ? (
            <input key={k} type="hidden" name={k} value={params.get(k) ?? ''} />
          ) : null,
        )}
        <div className="fs-filter-actions">
          <button type="submit">应用筛选</button>
          <a href={FIRE_SIGNALS_ROOT}>重置</a>
        </div>
      </form>
      <ReadNotice loading={loading} error={error} retry={retry} />
      {data && (
        <>
          <div className="fs-kpis">
            {[
              ['卫星候选', data.summary.counts.candidates],
              ['关联合格天气', data.summary.counts.linked],
              ['未关联 / 资料不足', data.summary.counts.unlinked],
              ['独立事件参照', data.summary.counts.eventReferences],
            ].map(([label, count]) => (
              <article key={label}>
                <span>{label}</span>
                <strong>{count}</strong>
              </article>
            ))}
          </div>
          <section className="fs-card">
            <div className="fs-section-title">
              <div>
                <p className="fs-eyebrow">HISTORICAL RESEARCH · NASA + NOAA</p>
                <h2>卫星像元地理分布</h2>
              </div>
              <div className="fs-map-actions">
                <a
                  aria-label="放大地图范围"
                  href={updateHref(search, { bbox: zoomBbox(bbox, 0.5).join(','), offset: null })}
                >
                  ＋ 放大
                </a>
                <a
                  aria-label="缩小地图范围"
                  href={updateHref(search, { bbox: zoomBbox(bbox, 2).join(','), offset: null })}
                >
                  − 缩小
                </a>
                <a href={updateHref(search, { bbox: null, offset: null })}>研究区全景</a>
              </div>
            </div>
            <p className="fs-caption">
              相同筛选范围内共 {data.page.total} 条 · 当前地图与列表显示 {data.page.items.length}{' '}
              条。所有时间均为历史 UTC。
            </p>
            <GeographicMap
              areas={data.summary.areas}
              candidates={data.page.items}
              stations={data.summary.stations}
              bbox={bbox}
              search={search}
            />
          </section>
          <section className="fs-card">
            <div className="fs-section-title">
              <h2>候选预览</h2>
              <span>
                {data.page.total
                  ? `${offset + 1}–${offset + data.page.items.length} / ${data.page.total}`
                  : '0 条'}
              </span>
            </div>
            {data.page.items.length === 0 ? (
              <p role="status">
                此范围暂无历史候选。可能尚未导入资料或筛选范围无记录；不代表没有火灾或风险。
              </p>
            ) : (
              <ul className="fs-preview">
                {data.page.items.map((c) => (
                  <li key={c.candidateId}>
                    <a
                      href={signalHref(search, c.candidateId)}
                      data-list-candidate-id={c.candidateId}
                    >
                      <span>
                        <strong>
                          {c.satellite.satellite} / {c.satellite.instrument}
                        </strong>
                        <small>{c.satellite.acquiredAtUTC}</small>
                      </span>
                      <span>
                        {c.satellite.latitude}, {c.satellite.longitude}
                        <small>{STATUS_LABELS[c.association.status]}</small>
                      </span>
                      <span aria-hidden="true">↗</span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
            <nav className="fs-paging" aria-label="候选分页">
              {offset > 0 && (
                <a href={updateHref(search, { offset: String(Math.max(0, offset - 8)) })}>上一页</a>
              )}
              {offset + 8 < data.page.total && (
                <a href={updateHref(search, { offset: String(offset + 8) })}>下一页</a>
              )}
            </nav>
          </section>
        </>
      )}
    </>
  );
}
function DetailPage({ id, search }: { id: string; search: string }) {
  const read = useCallback(
    async (signal: AbortSignal) => {
      const [candidate, sources] = await Promise.all([
        adapter.candidate(id, signal),
        adapter.sources(signal),
      ]);
      return { candidate, sources };
    },
    [id],
  );
  const state = useRead(read);
  return (
    <>
      <a className="fs-back" href={signalHref(search)}>
        ← 返回地图，保留筛选与分页
      </a>
      <ReadNotice loading={state.loading} error={state.error} retry={state.retry} />
      {state.data && (
        <CandidateDetail candidate={state.data.candidate} sources={state.data.sources.items} />
      )}
    </>
  );
}
export function FireSignalsPage() {
  const path = typeof window === 'undefined' ? FIRE_SIGNALS_ROOT : window.location.pathname;
  const search = typeof window === 'undefined' ? '' : window.location.search;
  const suffix = path.slice(FIRE_SIGNALS_ROOT.length).replace(/\/$/, '');
  let id: string | null = null;
  let invalid = false;
  if (suffix) {
    try {
      if (!/^\/candidates\/[^/]+$/.test(suffix)) invalid = true;
      else id = decodeURIComponent(suffix.slice('/candidates/'.length));
    } catch {
      invalid = true;
    }
  }
  return (
    <div className="fs-page">
      <PlatformHeader activePage="monitoring" />
      <main>
        <nav aria-label="当前位置">
          <a href="/normal-operations/risk-warning">风险与预警</a>
          <span> / 历史卫星关联</span>
        </nav>
        <header className="fs-heading">
          <p className="fs-eyebrow">HISTORICAL SATELLITE + WEATHER CONTEXT</p>
          <h1>
            卫星热异常与气象关联<span>（历史研究）</span>
          </h1>
          <p>可回溯的卫星观测与附近气象背景。每条候选保留其真实时间、位置和来源。</p>
        </header>
        <p className="fs-safety" role="note">
          {FIRE_SIGNALS_WARNING}
        </p>
        {invalid ? (
          <section className="fs-notice">
            <h2>404 · 无效的候选路径</h2>
            <a href={signalHref(search)}>返回地图</a>
          </section>
        ) : id ? (
          <DetailPage id={id} search={search} />
        ) : (
          <ListPage search={search} />
        )}
        <footer>研究用途 · 不产生作战任务或命令 · 热异常不等于已确认火灾</footer>
      </main>
    </div>
  );
}
